import React, { useEffect, useState, ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { User, ProtectedRouteProps } from "../types/auth";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [selectedOrgID, setSelectedOrgID] = useState<number | null>(
    localStorage.getItem("selectedOrgID") ? Number(localStorage.getItem("selectedOrgID")) : null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [hasValidToken, setHasValidToken] = useState<boolean>(false);
  const location = useLocation();

  const token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");
  const userID = localStorage.getItem("userID");

  useEffect(() => {
    const checkTokenState = async () => {
      if (!refreshToken) {
        setLoading(false);
        setHasValidToken(false);
        return;
      }

      let currentToken = token;
      let isTokenValid = false;

      if (currentToken) {
        isTokenValid = await validateToken(currentToken);
      }
      
      if (!isTokenValid && refreshToken) {
        const newToken = await refreshAuthToken();
        if (newToken) {
          currentToken = newToken;
          isTokenValid = true;
          await fetchAndSetUserData(currentToken);
        }
      } else if (isTokenValid && currentToken) {
        await fetchAndSetUserData(currentToken);
      }

      setHasValidToken(isTokenValid);
      setLoading(false);
    };

    setLoading(true);
    setHasValidToken(false);
    checkTokenState();
  }, [selectedOrgID, location.pathname, token]);

  const fetchAndSetUserData = async (currentToken: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${userID}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      if (response.ok) {
        const data: User = await response.json();
        setUser(data);

        if (!selectedOrgID && data.organizations && data.organizations.length > 0) {
          const firstOrgID = data.organizations[0].org_id;
          setSelectedOrgID(firstOrgID);
          localStorage.setItem("selectedOrgID", firstOrgID.toString());
        }
        return true;
      } else {
        localStorage.clear();
        return false;
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      return false;
    }
  };

  const validateToken = async (currentToken: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/validate`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      return response.ok;
    } catch (err) {
      console.error("Error validating token:", err);
      return false;
    }
  };

  const refreshAuthToken = async (): Promise<string | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.token);
        return data.token;
      } else {
        localStorage.clear();
        return null;
      }
    } catch (err) {
      console.error("Error refreshing token:", err);
      localStorage.clear();
      return null;
    }
  };

  const handleOrgChange = (newOrgID: number) => {
    setSelectedOrgID(newOrgID);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!token && !refreshToken) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  if (!hasValidToken) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  return children({ 
    user, 
    setUser, 
    selectedOrgID, 
    onOrgChange: handleOrgChange, 
    token 
  });
};

export default ProtectedRoute; 